import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Mail, MousePointer, 
  FileSignature, Moon, CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const TOUR_STORAGE_KEY = 'netlen-tour-completed';

const tourSteps = [
  {
    id: 'welcome',
    title: 'Netlen Webmail\'e Hoş Geldiniz!',
    description: 'Bu kısa tur ile temel özellikleri öğrenin. İstediğiniz zaman atlayabilirsiniz.',
    icon: Mail,
    position: 'center',
    page: 'mail'
  },
  {
    id: 'compose',
    title: 'Yeni Mail Oluşturma',
    description: 'Sol menüdeki "Yeni Mail" butonuna tıklayarak mail yazabilirsiniz. Kime alanında kişilerinizden otomatik öneri gelir.',
    icon: Mail,
    target: '[data-testid="compose-button"]',
    position: 'right',
    page: 'mail'
  },
  {
    id: 'select',
    title: 'Mail Seçimi',
    description: 'Maillerin avatarına tıklayarak tek tek seçim yapabilirsiniz. Seçili mailler için toplu işlem (silme, arşivleme) yapabilirsiniz.',
    icon: MousePointer,
    target: '[data-testid="mail-list"]',
    position: 'right',
    page: 'mail'
  },
  {
    id: 'signature',
    title: 'Mail İmzası',
    description: 'Bu sayfadan profesyonel görünümlü imzalar oluşturabilirsiniz. 3 hazır şablon ve özel HTML desteği mevcut.',
    icon: FileSignature,
    target: '[data-testid="settings-signature"]',
    position: 'right',
    page: 'settings',
    settingsSection: 'signature'
  },
  {
    id: 'theme',
    title: 'Tema Değiştirme',
    description: 'Görünüm ayarlarından açık/koyu tema arasında geçiş yapabilirsiniz.',
    icon: Moon,
    target: '[data-testid="settings-appearance"]',
    position: 'right',
    page: 'settings',
    settingsSection: 'appearance'
  },
  {
    id: 'complete',
    title: 'Hazırsınız!',
    description: 'Artık Netlen Webmail\'i kullanmaya başlayabilirsiniz. Sorularınız için destek ekibimize ulaşabilirsiniz.',
    icon: CheckCircle2,
    position: 'center',
    page: 'mail'
  }
];

export const OnboardingTour = ({ onComplete, onNavigate, onSettingsSection }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!completed) {
      setIsVisible(true);
    }
  }, []);

  // Üst bileşen her render'da yeni işlev verir; efekt yalnızca adım değişince çalışsın diye son değerler ref'te tutulur
  // (aksi halde her gezinme yeniden render + yeniden gezinme döngüsü yaratır)
  const onNavigateRef = useRef(onNavigate);
  const onSettingsSectionRef = useRef(onSettingsSection);
  onNavigateRef.current = onNavigate;
  onSettingsSectionRef.current = onSettingsSection;

  useEffect(() => {
    if (!isVisible) return undefined;

    const step = tourSteps[currentStep];

    if (step.page) onNavigateRef.current?.(step.page);

    let timer;
    if (step.settingsSection) {
      timer = setTimeout(() => onSettingsSectionRef.current?.(step.settingsSection), 100);
    }
    return () => clearTimeout(timer);
  }, [currentStep, isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    
    const updateTargetRect = () => {
      const step = tourSteps[currentStep];
      if (step.target && step.position !== 'center') {
        const element = document.querySelector(step.target);
        if (element) {
          const rect = element.getBoundingClientRect();
          setTargetRect(rect);
        } else {
          setTargetRect(null);
        }
      } else {
        setTargetRect(null);
      }
    };

    updateTargetRect();
    const timeout = setTimeout(updateTargetRect, 300);
    
    return () => clearTimeout(timeout);
  }, [currentStep, isVisible]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setIsVisible(false);
    if (onNavigate) onNavigate('mail');
    onComplete?.();
  };

  const handleComplete = () => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setIsVisible(false);
    if (onNavigate) onNavigate('mail');
    onComplete?.();
  };

  if (!isVisible) return null;

  const step = tourSteps[currentStep];
  const Icon = step.icon;
  const isCenter = step.position === 'center';

  const getTooltipStyle = () => {
    if (!targetRect || isCenter) {
      return {};
    }

    switch (step.position) {
      case 'right':
        return {
          top: Math.min(targetRect.top + targetRect.height / 2, window.innerHeight - 250),
          left: Math.min(targetRect.right + 20, window.innerWidth - 400),
          transform: 'translateY(-50%)'
        };
      case 'left':
        return {
          top: targetRect.top + targetRect.height / 2,
          right: window.innerWidth - targetRect.left + 20,
          transform: 'translateY(-50%)'
        };
      case 'top':
        return {
          bottom: window.innerHeight - targetRect.top + 20,
          left: targetRect.left + targetRect.width / 2,
          transform: 'translateX(-50%)'
        };
      case 'bottom':
        return {
          top: targetRect.bottom + 20,
          left: targetRect.left + targetRect.width / 2,
          transform: 'translateX(-50%)'
        };
      default:
        return {};
    }
  };

  const TooltipContent = () => (
    <div className="bg-card rounded-2xl shadow-2xl border border-border overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold">{step.title}</h3>
              <p className="text-xs text-white/70">
                Adım {currentStep + 1} / {tourSteps.length}
              </p>
            </div>
          </div>
          <button
            onClick={handleSkip}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            data-testid="tour-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {step.description}
        </p>
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-1.5 pb-3">
        {tourSteps.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === currentStep ? 'bg-blue-500' : 'bg-muted'
            }`}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-4 border-t border-border bg-muted/30">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSkip}
          className="text-muted-foreground"
          data-testid="tour-skip"
        >
          Atla
        </Button>
        <div className="flex items-center gap-2">
          {currentStep > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrev}
              data-testid="tour-prev"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Geri
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleNext}
            data-testid="tour-next"
          >
            {currentStep === tourSteps.length - 1 ? (
              'Başla'
            ) : (
              <>
                İleri
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[100]"
          />

          {/* Spotlight on target */}
          {targetRect && !isCenter && (
            <motion.div
              key={`spotlight-${currentStep}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed z-[101] pointer-events-none"
              style={{
                top: targetRect.top - 8,
                left: targetRect.left - 8,
                width: targetRect.width + 16,
                height: targetRect.height + 16,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
                borderRadius: '12px',
                border: '2px solid rgba(59, 130, 246, 0.5)'
              }}
            />
          )}

          {/* Tooltip - Center positioned */}
          {isCenter && (
            <motion.div
              key={`tooltip-center-${currentStep}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed z-[102] w-[380px] max-w-[calc(100vw-32px)] top-[40%] left-1/2 -translate-x-1/2 -translate-y-1/2"
              data-testid="tour-tooltip"
            >
              <TooltipContent />
            </motion.div>
          )}

          {/* Tooltip - Target positioned */}
          {!isCenter && (
            <motion.div
              key={`tooltip-target-${currentStep}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed z-[102] w-[380px] max-w-[calc(100vw-32px)]"
              style={getTooltipStyle()}
              data-testid="tour-tooltip"
            >
              <TooltipContent />
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default OnboardingTour;
